import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { flexRender } from '@tanstack/react-table';
import cx from 'clsx';
import type React from 'react';
import { DraggableRow } from './DraggableRow.tsx';

type StandardTableBodyProps = {
  virtualizer: any;
  virtualRows: any[];
  rows: any[];
  onRowClickHandler: (row: any) => (event: React.MouseEvent) => void;
  onRowMouseEnterHandler: (row: any) => (event: React.MouseEvent) => void;
  onRowMouseLeaveHandler: (row: any) => (event: React.MouseEvent) => void;
};

type ReorderableTableBodyProps = {
  rows: any[];
  tableData: any[];
};

const renderCell = (cell: any) => {
  const hasCustomCell = cell.column.columnDef.cell && cell.column.columnDef.cell !== cell.column.columnDef.defaultCell;

  if (hasCustomCell) {
    const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
    if (cell.column.columnDef.meta?.truncate) return <span className="truncate">{cellContent}</span>;
    return cellContent;
  }

  if (!cell.column.columnDef.meta?.hideFallback && (cell.renderValue() === null || cell.renderValue() === undefined)) {
    return <span className={cx(cell.column.columnDef.meta?.fallbackClassName)}>{cell.column.columnDef.meta?.fallback || '-'}</span>;
  }

  return cell.renderValue();
};

export const StandardTableBody: React.FC<StandardTableBodyProps> = ({ virtualizer, virtualRows, rows, onRowClickHandler, onRowMouseEnterHandler, onRowMouseLeaveHandler }) => (
  <tbody data-role="tbody" className="relative z-10 block" style={{ height: `${virtualizer.getTotalSize()}px` }}>
    {virtualRows.map((virtualRow, index) => {
      const row = rows[virtualRow.index];
      return (
        <tr
          data-role="tr"
          ref={(node) => virtualizer.measureElement(node)}
          key={index}
          data-index={virtualRow.index}
          className="absolute flex w-full cursor-pointer place-content-between border-grey-border border-b align-top last:border-b-0 hover:bg-gray-100"
          style={{ transform: `translateY(${virtualRow.start}px)` }}
          onClick={onRowClickHandler(row)}
          onMouseEnter={onRowMouseEnterHandler(row)}
          onMouseLeave={onRowMouseLeaveHandler(row)}
        >
          {row.getVisibleCells().map((cell) => (
            <td data-role="td" key={cell.id} className={cx('flex items-baseline truncate p-3', cell.column.columnDef.meta?.className)} style={{ width: cell.column.getSize() }}>
              {renderCell(cell)}
            </td>
          ))}
        </tr>
      );
    })}
  </tbody>
);

// Renders all rows without virtualisation — dnd-kit requires normal document flow to work correctly.
export const ReorderableTableBody: React.FC<ReorderableTableBodyProps> = ({ rows, tableData }) => (
  <tbody data-role="tbody" className="relative z-10 block">
    <SortableContext items={tableData.map((item) => item.id)} strategy={verticalListSortingStrategy}>
      {rows.map((row) => (
        <DraggableRow key={row.original.id} row={row.original}>
          {row.getVisibleCells().map((cell) => (
            <td data-role="td" key={cell.id} className={cx('flex items-baseline truncate p-3', cell.column.columnDef.meta?.className)} style={{ width: cell.column.getSize() }}>
              {renderCell(cell)}
            </td>
          ))}
        </DraggableRow>
      ))}
    </SortableContext>
  </tbody>
);
